import { logger } from '@utils/safeLogger'
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICallDirection, ICalls } from '@dataTypes/calls.type'
import axios from './axiosConfig'

import { getEnv } from '@utils/env'
import { toApiError } from './errors/api-error'

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type GetAllCallsResponse = {
  statusCode: number
  message: string
  data: {
    calls?: ICalls[]
  }
}

export type UpdateCallResponse = {
  statusCode: number
  message: string
  data: {
    calls?: ICalls[]
  }
}

export type CreateCallResponse = {
  statusCode: number
  message: string
  data: object
}

export type GetCallIdResponse = {
  statusCode: number
  message: string
  data: {
    call_id?: string
  }
}

export type GenericResponse = {
  statusCode: number
  message: string
  data: object
}

const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_CALLS')
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CALLS')

const headers = {
  'Content-Type': 'application/json',
}

// Function to create a call record
export const createCall = async (
  callDetails: Partial<ICalls>,
): Promise<CreateCallResponse> => {
  try {
    const userId = JSON.parse(localStorage.getItem('user') || '{}')
      .user_id as string
    const response = await axios.post<CreateCallResponse>(
      `${BUSINESS_LAYER_API}/api/v1/calls`,
      { callData: callDetails, userId },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to get all calls
export const getAllCalls = async (): Promise<GetAllCallsResponse> => {
  try {
    const response = await axios.get<GetAllCallsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/calls/all`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getCallRecordings = async (params: {
  clinic_id: string
  days?: number
  search?: string
  type?: ICallDirection
}): Promise<GetAllCallsResponse> => {
  try {
    const response = await axios.get<GetAllCallsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/calls/recordings`,
      { headers, params },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to update call by ID
export const updateCallById = async (
  call_id: string,
  updatedData: Partial<ICalls>,
): Promise<UpdateCallResponse> => {
  try {
    const response = await axios.put<UpdateCallResponse>(
      `${BUSINESS_LAYER_API}/api/v1/calls`,
      {
        call_id,
        data: updatedData,
      },
      { headers },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to update call by Number
export const updateCallByNumber = async (
  caller_number: string,
  receiver_number: string,
  updatedData: Partial<ICalls>,
): Promise<UpdateCallResponse> => {
  try {
    const response = await axios.put<UpdateCallResponse>(
      `${BUSINESS_LAYER_API}/api/v1/calls`,
      {
        caller_number,
        receiver_number,
        data: updatedData,
      },
      { headers },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to fetch call id
export const getCallId = async (
  callDetails: Partial<ICalls>,
): Promise<GetCallIdResponse> => {
  try {
    const response = await axios.put<GetCallIdResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/call-id`,
      { call: callDetails },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const streamCallRecording = async (
  blobName: string,
): Promise<string> => {
  const response = await axios.get(
    `${EXTERNAL_LAYER_API}/api/v1/external/calls/recordings/stream`,
    {
      headers,
      params: { b: blobName },
      responseType: 'blob',
    },
  )
  return URL.createObjectURL(response.data)
}

// Function to fetch presigned url for each call log
export const getBulkPresignedUrl = async (
  callDetails: ICalls[],
): Promise<GetAllCallsResponse> => {
  try {
    const response = await axios.put<GetAllCallsResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/bulk-presigned-url`,
      { calls: callDetails },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to ring a contact number
export const dialNumber = async (
  sid: string,
  token: string,
  conferenceName: string,
  contactNumber: string,
  fromNumber: string,
): Promise<CreateCallResponse> => {
  try {
    const response = await axios.post<CreateCallResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/dial`,
      {
        sid,
        token,
        conferenceName,
        to: contactNumber,
        from: fromNumber,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const dialExtension = async (
  sid: string,
  token: string,
  conferenceName: string,
  fromNumber: string,
  clinicId: string,
  extensionNumber: string,
  dbName: string,
): Promise<CreateCallResponse> => {
  try {
    const response = await axios.post<CreateCallResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/dial-extension`,
      {
        sid,
        token,
        conferenceName,
        from: fromNumber,
        clinic_id: clinicId,
        extension_number: extensionNumber,
        db_name: dbName,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const startRecording = async (
  conferenceName: string,
  contactNumber: string,
): Promise<CreateCallResponse> => {
  try {
    const response = await axios.post<CreateCallResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/dial`,
      { conferenceName, contactNumber },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const startConferenceRecordingApi = async (body: {
  sid: string
  token: string
  conferenceSid: string
  db_name: string
  clinic_id: string
  direction: ICallDirection
}): Promise<CreateCallResponse> => {
  try {
    const response = await axios.post<CreateCallResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/start-conference-recording`,
      body,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const stopConferenceRecordingApi = async (body: {
  sid: string
  token: string
  conferenceSid: string
}): Promise<CreateCallResponse> => {
  try {
    const response = await axios.post<CreateCallResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/stop-conference-recording`,
      body,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const setCallOnHold = async (
  sid: string,
  token: string,
  conferenceSid: string,
  onHold: boolean,
  db_name?: string,
  clinic_id?: string,
): Promise<CreateCallResponse> => {
  try {
    const response = await axios.post<CreateCallResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/hold`,
      { sid, token, conferenceSid, onHold, db_name, clinic_id },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to terminate existing call
export const endCallConference = async (
  sid: string,
  token: string,
  conferenceSid: string,
): Promise<GenericResponse> => {
  try {
    const response = await axios.post<GenericResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/calls/end-call`,
      { sid, token, conferenceSid },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteCallById = async (
  callId: string,
): Promise<CreateCallResponse> => {
  try {
    const response = await axios.delete<CreateCallResponse>(
      `${BUSINESS_LAYER_API}/api/v1/calls/${callId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteCallById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteCallRecording = async (
  callId: string,
): Promise<ApiResponse<{ call_id?: string }>> => {
  try {
    const response = await axios.delete<ApiResponse<{ call_id?: string }>>(
      `${BUSINESS_LAYER_API}/api/v1/calls/${callId}/recording`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteCallRecording:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
